import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { Modal } from 'shared/ui/Modal';
import { InfoTag } from 'shared/ui/InfoTag';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { useGetContractVerificationDocumentsQuery } from 'entities/VerificationPhoto';
import { UploadDocument } from 'features/UploadDocument';
import { getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import dayjs from 'dayjs';

interface MoveOutPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  lastBoxOpening?: string;
}

export const MoveOutPhotosModal: React.FC<MoveOutPhotosModalProps> = (props) => {
  const { isOpen, onClose, contractId, lastBoxOpening } = props;
  const { t } = useAppTranslation(['common', 'contracts']);

  const { data: documents, isFetching } = useGetContractVerificationDocumentsQuery({ contractId }, { skip: !contractId });

  const { isDesktop } = useScreenBreakpoints();

  const lastUploadedPhoto = useMemo(() => {
    if (!documents?.length) {
      return null;
    }

    return [...documents].sort((a, b) => dayjs(b.uploadDate).valueOf() - dayjs(a.uploadDate).valueOf())[0];
  }, [documents]);

  const showInfoTag = useMemo(() => {
    if (!lastBoxOpening || !lastUploadedPhoto?.uploadDate) {
      return false;
    }

    const openingDate = dayjs(lastBoxOpening);
    const photoDate = dayjs(lastUploadedPhoto.uploadDate);

    if (!openingDate.isValid() || !photoDate.isValid()) {
      return false;
    }

    return openingDate.isAfter(photoDate);
  }, [lastBoxOpening, lastUploadedPhoto]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={isDesktop ? 980 : 380}>
      <div className="font-semibold text-xl mb-3">{t('Move out photos', { ns: 'contracts' })}</div>

      <div className="flex flex-col desktop:flex-row desktop:items-center my-2 desktop:gap-6">
        {lastUploadedPhoto?.uploadDate && (
          <div className="flex gap-1">
            {t('Last uploaded photo', { ns: 'contracts' })}:
            <span className="font-semibold text-accent">{getClientDateWithMinuteTimeFormat(lastUploadedPhoto.uploadDate)}</span>
          </div>
        )}

        {lastBoxOpening ? (
          <div className="flex gap-1">
            {t('Last box opening', { ns: 'contracts' })}:
            <span className="font-semibold text-accent">{getClientDateWithMinuteTimeFormat(lastBoxOpening)}</span>
          </div>
        ) : (
          t('The box was not opened during the rental period!', { ns: 'contracts' })
        )}
      </div>

      {showInfoTag && (
        <InfoTag textSize="medium">
          {t('The box was opened after the last uploaded photo. Please upload a new photo!', {
            ns: 'contracts',
          })}
        </InfoTag>
      )}

      {isFetching ? (
        <div className="flex justify-center items-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <UploadDocument documents={documents} onSuccess={onClose} />
      )}
    </Modal>
  );
};
